"use client";

import Link from "next/link";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { SiteHeader } from "@/components/SiteHeader";
import {
  privacyPolicyForLocale,
  type PrivacyPolicyVariant,
} from "@/lib/legal/privacyContent";

/**
 * EPIC-050 STORY-2211 — public Privacy Policy page.
 *
 * Mirrors the /legal/tos page pattern but loads its body from a
 * locale-keyed content module rather than a backend endpoint, since
 * privacy text is jurisdiction-scoped (LGPD framing for pt-BR,
 * generic phrasing for default English) and the locked rule requires
 * no cross-contamination between locales (CLAUDE.md 2026-05-03).
 *
 * Phase 1 ships en + pt-BR scaffolds (DRAFT pending lawyer review).
 * Other locales fall back to the en variant via privacyPolicyForLocale.
 */
export default function PrivacyPage() {
  const { t, i18n } = useTranslation();
  const policy: PrivacyPolicyVariant = useMemo(
    () => privacyPolicyForLocale(i18n.language ?? "en"),
    [i18n.language],
  );

  return (
    <>
      <SiteHeader />
      <main className="mx-auto max-w-[800px] px-5 py-8">
        <Link
          href="/help"
          className="text-[12px] text-[var(--color-text3)] hover:text-[var(--color-text)]"
        >
          {t("privacyPage.backToHelp", "← Back to help")}
        </Link>

        <article className="mt-3">
          <header className="mb-6 flex flex-wrap items-baseline justify-between gap-2 border-b border-[var(--color-border)] pb-4">
            <div>
              <p className="text-[10px] uppercase tracking-[0.4px] text-[var(--color-text3)]">
                {t("privacyPage.kicker", "Privacy")}
              </p>
              <h1 className="mt-0.5 text-[24px] font-extrabold tracking-tight">
                {t("privacyPage.title", "Privacy Policy")}
              </h1>
            </div>
            <p className="text-[11px] text-[var(--color-text3)]">
              {t("privacyPage.effectivePrefix", "Effective")}{" "}
              {new Date(policy.effectiveDate).toLocaleDateString()}
            </p>
          </header>

          <p className="mb-5 rounded-md border border-[var(--color-border)] bg-[var(--color-bg)] p-3 text-[12px] text-[var(--color-text2)]">
            <strong>{t("privacyPage.summaryLabel", "Summary")}:</strong>{" "}
            {policy.summary}
          </p>

          <PolicyBody bodyMd={policy.bodyMd} />
        </article>
      </main>
    </>
  );
}

/**
 * Inline markdown renderer covering: # / ## headings, paragraphs,
 * lists with `-` bullets, bold (**foo**), italics (_foo_), hr (---).
 * Mirrors the /legal/tos page renderer so visual rendering stays
 * consistent. Promote to react-markdown when content complexity
 * outgrows this set.
 */
function PolicyBody({ bodyMd }: { bodyMd: string }) {
  const lines = bodyMd.split("\n");
  const blocks: React.ReactNode[] = [];
  let para: string[] = [];
  let listItems: string[] = [];

  const flushPara = () => {
    if (para.length === 0) return;
    blocks.push(
      <p
        key={`p-${blocks.length}`}
        className="mb-3 text-[14px] leading-relaxed text-[var(--color-text2)]"
        dangerouslySetInnerHTML={{ __html: renderInline(para.join(" ")) }}
      />,
    );
    para = [];
  };

  const flushList = () => {
    if (listItems.length === 0) return;
    blocks.push(
      <ul
        key={`ul-${blocks.length}`}
        className="mb-3 ml-5 list-disc space-y-1 text-[14px] leading-relaxed text-[var(--color-text2)]"
      >
        {listItems.map((item, i) => (
          <li
            key={i}
            dangerouslySetInnerHTML={{ __html: renderInline(item) }}
          />
        ))}
      </ul>,
    );
    listItems = [];
  };

  for (const line of lines) {
    if (line.startsWith("# ")) {
      flushPara();
      flushList();
      blocks.push(
        <h1
          key={`h1-${blocks.length}`}
          className="mt-6 mb-3 text-[20px] font-extrabold tracking-tight"
        >
          {line.slice(2)}
        </h1>,
      );
    } else if (line.startsWith("## ")) {
      flushPara();
      flushList();
      blocks.push(
        <h2
          key={`h2-${blocks.length}`}
          className="mt-5 mb-2 text-[16px] font-bold tracking-tight"
        >
          {line.slice(3)}
        </h2>,
      );
    } else if (line.startsWith("- ")) {
      flushPara();
      listItems.push(line.slice(2));
    } else if (line.trim() === "") {
      flushPara();
      flushList();
    } else if (line.trim() === "---") {
      flushPara();
      flushList();
      blocks.push(
        <hr
          key={`hr-${blocks.length}`}
          className="my-5 border-[var(--color-border)]"
        />,
      );
    } else {
      flushList();
      para.push(line);
    }
  }
  flushPara();
  flushList();

  return <div>{blocks}</div>;
}

function renderInline(s: string): string {
  return escapeHtml(s)
    .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
    .replace(/_(.+?)_/g, "<em>$1</em>");
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}
